/**
 * Business classification — single source of truth.
 *
 * Every place that maps a `business_classification` string to a colour,
 * label, sub-label, or icon reads from here. Adding a fourth class in
 * the future only requires editing this file + the matching
 * `backend/constants/classification.py`.
 */
import { Sun, Factory, UserCircle2, Building2, Landmark, HandCoins } from "lucide-react";

export const DEFAULT_CLASS = "retro";

// Ordered so the UI + PDF render columns/cards in the same sequence.
export const CLASSIFICATIONS = [
  { key: "retro",       label: "Retro",       sub: "Existing sites",       tone: "#0F8B84", icon: Sun },
  { key: "development", label: "Development", sub: "New-build pipeline",   tone: "#B8721E", icon: Factory },
  { key: "customer",    label: "Customer",    sub: "Third-party sites",    tone: "#5B4B8A", icon: UserCircle2 },
  { key: "acquisition", label: "Acquisition", sub: "Acquired assets",      tone: "#2C5AA0", icon: Landmark },
  { key: "forward_fund", label: "Forward Fund", sub: "Forward-funded sites", tone: "#B33C86", icon: HandCoins },
];
export const CLASS_KEYS = CLASSIFICATIONS.map(c => c.key);

// Also exported as a keyed lookup so consumers can go
// `CLASS_BY_KEY[project.business_classification]` in one line.
export const CLASS_BY_KEY = Object.fromEntries(
  CLASSIFICATIONS.map(c => [c.key, c])
);

// Extra entries for "Combined" — used only by the toggles + create modal
// (never a real classification value on a project).
export const COMBINED_META = {
  key: "combined",
  label: "Combined",
  sub: "All classifications",
  tone: "#1F1A66",
  icon: Building2,
};

/** Normalise any string → a known classification key. Unknown → DEFAULT_CLASS. */
export function resolveClass(value) {
  if (!value) return DEFAULT_CLASS;
  const v = String(value).toLowerCase().trim();
  return CLASS_BY_KEY[v] ? v : DEFAULT_CLASS;
}

/** Return the accent hex for a classification key. */
export function classTone(key) {
  return (CLASS_BY_KEY[key] || CLASS_BY_KEY[DEFAULT_CLASS]).tone;
}

/** Row-tag style descriptor for a class (background + foreground). Used
 *  by tables/lists that render coloured pills next to project names. */
export function classTag(key) {
  const meta = CLASS_BY_KEY[key];
  if (!meta) return null;
  // Alpha-blended background derived from the tone so we don't have to
  // hand-pick a 6th colour per classification.
  return { bg: `${meta.tone}22`, fg: meta.tone, label: meta.label };
}
